/**
 * Pure, day-granularity date helpers for the Research Cadence timers.
 *
 * Design invariants (do not "optimize" away):
 *  - All calendar arithmetic operates on UTC-noon `Date` anchors and uses
 *    `getUTC*`/`setUTC*` accessors exclusively — NEVER `.getDate()/.getDay()/
 *    .setDate()`. This keeps a calendar day from silently shifting under a
 *    non-US browser timezone or across a DST boundary.
 *  - `etTodayISO` reads "today" in America/New_York to match the engine's
 *    `now_et`; everything downstream is pure ISO-date string math.
 *  - Holiday-blind (matches the engine's `BDay`-based trading-day rule).
 */

/** Today's date in America/New_York as "YYYY-MM-DD" (matches engine `now_et`). */
export function etTodayISO(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(now)
}

/** Parse "YYYY-MM-DD" to a UTC-noon anchor (calendar math never crosses a day boundary). */
export function parseISO(d: string): Date {
  return new Date(`${d}T12:00:00Z`)
}

/** A UTC-anchored Date to "YYYY-MM-DD". */
export function fmtISO(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Human-friendly short date (e.g. "Aug 3"). `timeZone:'UTC'` is REQUIRED because
 * `parseISO` yields a UTC-noon anchor — omitting it formats in the browser's local
 * tz and can render the wrong day for a non-ET viewer. */
export function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parseISO(iso))
}

/** Advance an ISO date by `n` calendar days. */
export function addCalendarDays(iso: string, n: number): string {
  const d = parseISO(iso)
  d.setUTCDate(d.getUTCDate() + n)
  return fmtISO(d)
}

/** True if the ISO date falls on Saturday or Sunday. */
export function isWeekend(iso: string): boolean {
  const dow = parseISO(iso).getUTCDay()
  return dow === 0 || dow === 6
}

/**
 * Advance `n` weekdays (skip Sat=6/Sun=0), holiday-blind. n=0 returns the same
 * date if it's a weekday, else the next weekday.
 */
export function addBusinessDays(iso: string, n: number): string {
  const d = parseISO(iso)
  // Normalize to a weekday first (n=0 on a weekend rolls forward to Monday).
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) {
    d.setUTCDate(d.getUTCDate() + 1)
  }
  let remaining = n
  while (remaining > 0) {
    d.setUTCDate(d.getUTCDate() + 1)
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) {
      remaining -= 1
    }
  }
  return fmtISO(d)
}

/** First weekday (Mon-Fri) of a given year/month (month is 0-indexed). */
function firstWeekdayOfMonth(year: number, month: number): string {
  const d = new Date(Date.UTC(year, month, 1, 12, 0, 0))
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) {
    d.setUTCDate(d.getUTCDate() + 1)
  }
  return fmtISO(d)
}

/**
 * The upcoming monthly-discovery EXECUTION date: the first weekday of a month.
 * Discovery executes on the first trading day of the month (the `as_of`-lag
 * rule), so we show the first weekday of THIS month if it is still upcoming,
 * else the first weekday of NEXT month. Holiday-blind.
 */
export function nextDiscoveryISO(todayISO: string): string {
  const today = parseISO(todayISO)
  const year = today.getUTCFullYear()
  const month = today.getUTCMonth()
  const first = firstWeekdayOfMonth(year, month)
  if (todayISO <= first) return first
  // Roll to next month (December wraps to January of next year).
  const nextYear = month === 11 ? year + 1 : year
  const nextMonth = month === 11 ? 0 : month + 1
  return firstWeekdayOfMonth(nextYear, nextMonth)
}

/**
 * The soonest Tuesday >= today (if today is Tuesday, returns today). This is the
 * bench re-score EXECUTION day: the gate is `as_of.dayofweek==0` (Monday) and
 * `as_of` lags one session, so the Monday re-score runs on the Tuesday tick.
 */
export function nextTuesdayISO(todayISO: string): string {
  const d = parseISO(todayISO)
  // Tuesday === 2 in getUTCDay().
  const delta = (2 - d.getUTCDay() + 7) % 7
  d.setUTCDate(d.getUTCDate() + delta)
  return fmtISO(d)
}

/**
 * The next 00:00 UTC instant — the daily LLM news-scoring budget reset.
 *
 * Returns an INSTANT, not an ISO date. The previous `nextUtcMidnightISO`
 * returned the reset's *UTC calendar date*, which the strip then compared
 * against `etTodayISO()` — an *ET calendar date*. Because ET is UTC-4/-5 the
 * two are in different day-spaces, so the cell read "tomorrow" for 22h of
 * every day and "in 2d" for the other 2, and could never read "today".
 * A sub-daily event has no correct day-granularity label; render the
 * countdown with `formatDurationUntil` instead.
 */
export function nextUtcMidnight(now: Date = new Date()): Date {
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0),
  )
}

/**
 * Coarse forward-duration label: "in 4h", "in 43m", "in <1m", "now".
 * Hour granularity above 1h keeps the cell as terse as its day-granularity
 * siblings while still changing through the day.
 */
export function formatDurationUntil(ms: number): string {
  if (ms <= 0) return 'now'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'in <1m'
  if (minutes < 60) return `in ${minutes}m`
  return `in ${Math.floor(minutes / 60)}h`
}

/** Wall-clock time of an instant in America/New_York, e.g. "20:00 ET". */
export function etWallClock(at: Date): string {
  const hhmm = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(at)
  return `${hhmm} ET`
}

/** Integer calendar days `target - today` (can be 0 or negative). */
export function daysUntil(targetISO: string, todayISO: string): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000
  const diff = parseISO(targetISO).getTime() - parseISO(todayISO).getTime()
  return Math.round(diff / MS_PER_DAY)
}

/** Forward countdown label: <0 -> "overdue", 0 -> "today", 1 -> "tomorrow", else "in Nd". */
export function formatCadence(days: number): string {
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `in ${days}d`
}
